import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, catchError, combineLatest, finalize, forkJoin, map, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { Helper } from 'src/app/accountancy/utilities/classes/helper.class';
import { Routes } from 'src/app/accountancy/utilities/classes/routes.class';
import { WaitHandler } from 'src/app/accountancy/utilities/classes/wait-handler.class';
import { LiquidationInterface, LiquidationItem, LiquidationItemHistory } from 'src/app/accountancy/utilities/interfaces/liquidation.interface';
import { ApprovalStatusInterface } from '../../../../utilities/interfaces/global.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { LiquidationService } from '../liquidation-service/liquidation.service';
import { ManageApproversService } from '../../../account-management/manage-approvers/manage-approvers-service/manage-approvers.service';
import { AccSimpleInputTextFormService } from 'src/app/accountancy/custom-components/acc-simple-input-text-form/acc-simple-input-text-form-service/acc-simple-input-text-form.service';
import { AccMessageDialogService } from 'src/app/accountancy/custom-components/acc-message-dialog/acc-message-dialog-service/acc-message-dialog.service';
import { AccConfirmationService } from 'src/app/accountancy/custom-components/acc-confirmation/acc-confirmation-service/acc-confirmation.service';
import { GlobalApiService } from 'src/app/accountancy/utilities/services/global-api/global-api.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginService } from 'src/app/accountancy/authentication/login/login-service/login.service';
import { DisbursementVoucherComponent } from '../../disbursement-voucher/disbursement-voucher.component';
import { DisbursedService } from '../../disbursed-service/disbursed.service';
import { AccountingApiService } from 'src/app/accountancy/utilities/services/accounting-api/accounting-api.service';
import { ViewLiquidationItemHistoryComponent } from '../view-liquidation-item-history/view-liquidation-item-history.component';
import { LiquidationAddItemFormComponent } from '../liquidation-add-item-form/liquidation-add-item-form.component';

@Component({
  selector: 'app-view-liquidation',
  templateUrl: './view-liquidation.component.html',
  styleUrls: ['./view-liquidation.component.scss']
})
export class ViewLiquidationComponent {

  public readonly Helper = Helper;
  public readonly routes = Routes;
  public readonly rawLiqId = Helper.getIdOnSearchParam(this._aRoute, 'id');
  public readonly liqId = Helper.string.formatId.toNumber(this.rawLiqId);

  public readonly faLiqItems = new FormArray<FormGroup<{
    ItemId: FormControl<number | undefined | null>;
    ApprovedAmount: FormControl<number | undefined | null>;
    Remarks: FormControl<string | undefined | null>;
    Account: FormControl<string | undefined | null>;
  }>>([]);

  public readonly waitHandler = new WaitHandler();

  public readonly currentLiq$ = new BehaviorSubject<LiquidationInterface | undefined>(undefined);
  private readonly reloadCurrentLiq$ = new BehaviorSubject<undefined | void>(undefined);
  private readonly _subsGetCurrentLiq: Subscription = this._getSubsGetCurrentLiq();

  public readonly chartAccList$ = new BehaviorSubject<string[]>([]);
  private readonly _getChartAccListSubs: Subscription = this._getChartAccList();

  public readonly readInfo01$ = new BehaviorSubject<boolean>(false);

  constructor(
    private _aRoute: ActivatedRoute,
    private _router: Router,
    private _liqS: LiquidationService,
    private _mApprS: ManageApproversService,
    private _simpleFormS: AccSimpleInputTextFormService,
    private _msg: AccMessageDialogService,
    private _confirm: AccConfirmationService,
    private _gApiS: GlobalApiService,
    private _matDialog: MatDialog,
    private _globalApiS: GlobalApiService,
    private _disburseS: DisbursedService,
    private _accApi: AccountingApiService,

    public loginS: LoginService,
  ) { }

  public ngOnInit(): void {
    if (!this.liqId) this._router.navigate([this.routes.cashAdvance]);
  }

  public ngOnDestroy(): void {
    this._subsGetCurrentLiq.unsubscribe();
    this._getChartAccListSubs.unsubscribe();
  }

  public setAccountItem(index: number) {
    const fcAccount = this.getFormGroup(index).controls.Account;
    const isRequesting$ = new BehaviorSubject<boolean>(false);

    const categoryList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);
    const accountList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);
    const filteredAccountList$ = new BehaviorSubject<{ value: any, key: string }[]>([]);

    const subsCateg = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._accApi.chartOfAccounts.category.getCategoryList(0, 99999).pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsCateg.unsubscribe();
        }),
        map(data => data.body?.data ?? []),
        map(data => data.map(item => ({ value: item.Name, key: item.Name }))),
        tap(data => categoryList$.next(data))
      ))
    ).subscribe();

    const subsAcc = of(null).pipe(
      tap(() => isRequesting$.next(true)),
      switchMap(() => this._accApi.chartOfAccounts.getChartOfAccountList(0, 99999).pipe(
        finalize(() => {
          isRequesting$.next(false);
          subsAcc.unsubscribe();
        }),
        map(data => data.body?.data ?? []),
        map(data => data.map(item => ({ value: { Account: `(${item.Code}) ${item.Account}`, Category: item.Category.Name }, key: `(${item.Code}) ${item.Account}` }))),
        tap(data => accountList$.next(data))
      ))
    ).subscribe();

    const onCategoryChange = (categ: string) => {
      filteredAccountList$.next(
        accountList$.getValue().filter(acc => acc.value.Category === categ).map(acc => ({ key: acc.key, value: acc.value.Account }))
      );
    };

    this._simpleFormS.open({
      isRequesting$,
      title: "Set Account",
      controls: [
        { name: "Category", inputType: "select", colClassName: "col-span-12", selectOptions$: categoryList$, onValueChanges: onCategoryChange },
        { name: "Account", inputType: "search", colClassName: "col-span-12", selectOptions$: filteredAccountList$ },
      ],
      onSubmit: (formData, ref) => {
        fcAccount.patchValue(formData["Account"]);
        ref?.close();
      }
    });
  }

  public approveRequest() {
    const items = this.currentLiq$.getValue()?.Items ?? []
    const itemWithStatus = items.filter(item => item.Status);
    if (items.length != itemWithStatus.length) {
      this._msg.open({
        title: "Error occurred.",
        message: "All items must have a status.",
        messageType: "text-red-400"
      });
      return;
    }

    const isRequesting$ = new BehaviorSubject<boolean>(false);
    const ref = this._simpleFormS.open({
      isRequesting$,
      title: "Approve Request",
      formClass: "min-w-[400px]",
      controls: [
        { name: "Remarks", inputType: "text", colClassName: "col-span-12" }
      ],
      onSubmit: formData => {
        const subs: Subscription = of(null).pipe(
          tap(() => {
            this.waitHandler.newRequest();
            isRequesting$.next(true);
          }),
          switchMap(() => this._liqS.approveRequest(this.liqId, formData["Remarks"]).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              isRequesting$.next(false);
              ref.close();
              subs.unsubscribe();
            }),
          ))
        ).subscribe();
      },
    });

    const subsRef: Subscription = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentLiq$.next();
        subsRef.unsubscribe();
      }),
    ).subscribe();
  }

  public disapproveRequest() {
    const isRequesting$ = new BehaviorSubject<boolean>(false);
    const ref = this._simpleFormS.open({
      isRequesting$,
      title: "Disapprove Request",
      formClass: "min-w-[400px]",
      controls: [
        { name: "Remarks", inputType: "text", colClassName: "col-span-12" }
      ],
      onSubmit: formData => {
        const subs: Subscription = of(null).pipe(
          tap(() => {
            this.waitHandler.newRequest();
            isRequesting$.next(true);
          }),
          switchMap(() => this._liqS.disapproveRequest(this.liqId, formData["Remarks"]).pipe(
            finalize(() => {
              this.waitHandler.finishRequest();
              isRequesting$.next(false);
              ref.close();
              subs.unsubscribe();
            }),
          ))
        ).subscribe();
      },
    });

    const subsRef: Subscription = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentLiq$.next();
        subsRef.unsubscribe();
      }),
    ).subscribe();
  }

  public approveItem(index: number) {
    const formGroup = this.getFormGroup(index);
    if (!formGroup) return;

    const formData = formGroup.value;
    const errorControls = Helper.getInvalidControls(formGroup);
    if (errorControls.length) {
      this._msg.open({
        title: "Error occurred.",
        messageType: "text-red-400",
        message: [
          "Invalid values found in the fields below: ",
          ...errorControls.map(c => `- ${c.key}`),
        ]
      })
      return;
    }

    this._confirm.open({
      title: "Confirm Approval (Approved)",
      message: [
        `Are you sure you want to Approved this item with ₱ ${(Number(formData["ApprovedAmount"]))?.toFixed(2)} amount?`,
        "You cannot undo this action."
      ],
      onAnswer: (yes, ref) => {
        if (yes) {
          const subs: Subscription = of(null).pipe(
            tap(() => this.waitHandler.newRequest()),
            switchMap(() => this._liqS.updateItemStatus(
              this.liqId,
              formData["ItemId"] ?? -1,
              {
                Remarks: formData["Remarks"] ?? "",
                ApprovedAmount: formData["ApprovedAmount"] ?? -1,
                Status: "Approved",
                Account: formData["Account"],
              }
            ).pipe(
              finalize(() => {
                this.waitHandler.finishRequest();
                this.reloadCurrentLiq$.next();
                subs.unsubscribe();
              })
            ))
          ).subscribe();
        }

        ref.close();
      }
    });
  }

  public disapproveItem(index: number) {
    const formGroup = this.getFormGroup(index);
    if (!formGroup) return;

    const formData = formGroup.value;

    this._confirm.open({
      title: "Confirm Approval (Disapproved)",
      message: [
        "Are you sure you want to Disapproved this item?",
        "You cannot undo this action."
      ],
      onAnswer: (yes, ref) => {
        if (yes) {
          const subs: Subscription = of(null).pipe(
            tap(() => this.waitHandler.newRequest()),
            switchMap(() => this._liqS.updateItemStatus(
              this.liqId,
              formData["ItemId"] ?? -1,
              {
                Remarks: formData["Remarks"] ?? "",
                ApprovedAmount: 0,
                Status: "Disapproved",
                Account: undefined,
              }
            ).pipe(
              finalize(() => {
                this.waitHandler.finishRequest();
                this.reloadCurrentLiq$.next();
                subs.unsubscribe();
              })
            ))
          ).subscribe();
        }

        ref.close();
      }
    });
  }

  public viewItemHistory(index: number, data: LiquidationItemHistory[]) {
    const ref = this._matDialog.open(ViewLiquidationItemHistoryComponent, { data });
  }

  public addLiquidationItem() {
    const ref = this._matDialog.open(LiquidationAddItemFormComponent, { data: { liqId: this.liqId }, disableClose: true });
    const onCloseSubs: Subscription = ref.afterClosed().pipe(
      finalize(() => {
        this.reloadCurrentLiq$.next();
        onCloseSubs.unsubscribe();
      })
    ).subscribe();
  }

  public getFormGroup(index: number) {
    return this.faLiqItems.controls[index];
  }

  public viewVoucher() {
    this._disburseS.voucher.setCurrValue({
      type: "Liquidation",
      disbursementVoucher: this.currentLiq$.getValue()?.Voucher,
      controlNumber: this.rawLiqId,
    });
    this._router.navigate(['home', 'disbursement', 'disbursement-voucher']);
  }

  private _newItemFg(currItem: LiquidationItem) {
    const isForFinancerApproval = this.currentLiq$.getValue()?.AddChartOfAccountItem;
    const approvedAmount = currItem.ApprovedAmount || currItem.VatableSales + currItem.VAT;

    const fcItemId = Helper.reactiveForms.control<number | undefined | null>(currItem.ID, [Validators.required]);
    const fcApprovedAmount = Helper.reactiveForms.control<number | undefined | null>(approvedAmount, [Validators.required, Validators.min(1), Validators.max(currItem.VatableSales + currItem.VAT)]);
    const fcRemarks = Helper.reactiveForms.control<string | undefined | null>(undefined);
    const fcAccount = Helper.reactiveForms.control<string | undefined | null>(currItem.Account, isForFinancerApproval ? [Validators.required] : []);

    const liq = this.currentLiq$.getValue();
    const currStatus = currItem.Status;
    const itemHaveStatus = (currStatus && (currStatus == "Approved" || currStatus == "Disapproved")) ?? false;
    const userCanUpdateStatus = liq && (liq.update_status);
    if (itemHaveStatus || !userCanUpdateStatus) {
      if (currStatus) fcRemarks.patchValue(currItem.History.reverse()[0]?.Remarks, { emitEvent: false });
      fcApprovedAmount.disable();
      fcRemarks.disable();
      fcAccount.disable();
    }

    return new FormGroup({
      ItemId: fcItemId,
      ApprovedAmount: fcApprovedAmount,
      Remarks: fcRemarks,
      Account: fcAccount,
    });
  }

  private _getChartAccList() {
    return of(null).pipe(
      tap(() => this.waitHandler.newRequest()),
      switchMap(() => this._accApi.chartOfAccounts.getChartOfAccountList(0, 9999).pipe(
        finalize(() => this.waitHandler.finishRequest()),
        map(values => values.body?.data),
        tap(values => {
          const list = values?.map(v => `(${v.Code}) ${v.Category.Name} - ${v.Account}`) ?? [];

          this.chartAccList$.next(list);
        })
      ))
    ).subscribe();
  }

  private _getSubsGetCurrentLiq() {
    return combineLatest([
      this.reloadCurrentLiq$.pipe(startWith()),
    ]).pipe(
      tap(() => {
        this.waitHandler.newRequest();
        this.faLiqItems.clear();
      }),
      switchMap(() => this._liqS.getSingleLiquidation(this.liqId).pipe(
        finalize(() => this.waitHandler.finishRequest()),
      )),
      switchMap(rawData => {
        const rawItems = rawData?.Items ?? [];
        const getItemsProofURL = rawItems.map(item => of(null).pipe(
          tap(() => this.waitHandler.newRequest()),
          switchMap(() => this._globalApiS.getFileUrl(item.Proof, "Receipts").pipe(
            finalize(() => this.waitHandler.finishRequest()),
            map(proof => ({
              itemId: item.ID,
              proof: proof.body?.url ?? ''
            }))
          ))
        ));

        if (rawItems.length) return forkJoin(getItemsProofURL).pipe(
          map(proof => rawItems.map(item => {
            const newProof = proof.find(p => p.itemId === item.ID);
            if (newProof) return ({ ...item, ProofURL: newProof.proof })
            else return item;
          })),
          map(newItems => newItems.map(item => ({
            ...item,
            History: Helper.array.sortByPropertyName(item.History, "created_at", false)
          } as LiquidationItem))),
          map(orderedItems => ({
            ...rawData,
            Items: orderedItems,
            history: Helper.array.sortByPropertyName(rawData?.History, "created_at", false),
          } as LiquidationInterface)),
        )

        else return of(rawData as LiquidationInterface);
      }),
      tap(values => {
        this.currentLiq$.next(values);
      }),
      tap(values => values.Items.forEach(item => {
        const fgItem = this._newItemFg(item);
        this.faLiqItems.push(fgItem);
      })),
      catchError(() => this._globalApiS.handleSingleGetError(() => this._router.navigate([this.routes.reimbursement]))),
    ).subscribe();
  }

}
